/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

// Migrated from: lego/page/etmf/list/edit-packages/Step2/EditPackageStep2.jsx

import React, { useEffect, useState } from 'react'
import { components } from 'react-select'
import cloneDeep from 'lodash/cloneDeep'
import { pluck } from 'underscore'
import Modal from '../../Modal/index'
import { useServiceContext } from '../../../root'
import { Select } from '../../Select/Select'
import { transformToSelectOptions } from '../../../utils/selectOptions'
import { highlight_search } from '../../../utils/highlight'
import PackageArtifactSelector from '../PackageArtifactSelector'
import type { EditPackageStep2Props, PackageOptions, FilterOptions, SelectedFilters } from './types'

function excludeOverlap(set1: Set<number>, set2: Set<number>): Set<number> {
  const result = new Set<number>()
  Array.from(set1).forEach((item) => {
    if (!set2.has(item)) {
      result.add(item)
    }
  })
  return result
}

const EditPackageStep2: React.FC<EditPackageStep2Props> = ({
  packageData,
  options,
  nextStep,
  doClose,
  selectedFilters,
  setSelectedFilters,
  setPackageData,
  isApplyPackageFlow,
  manuallySelectedArtifactIds,
  setManuallySelectedArtifactIds
}) => {
  const context = useServiceContext()
  const { filter } = context.service
  const { translate } = filter
  const [selectedArtifacts, setSelectedArtifacts] = useState<any[]>([])
  const [arifactSearchString, setArifactSearchString] = useState('')
  const [optionsLocal, setOptionsLocal] = useState<PackageOptions>({})
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    rule: [],
    stage: [],
    milestone: []
  })
  const [filterSelectedArtifactIds, setFilterSelectedArtifactIds] = useState<Set<number>>(new Set())

  useEffect(() => {
    context.api.etmf.getLinkedEntities().then((data: any) => {
      Promise.all([
        context.api.etmf.propertyValuesSearch(data?.entityLinkMap?.RULE),
        context.api.etmf.propertyValuesSearch(data?.entityLinkMap?.STAGE),
        context.api.etmf.propertyValuesSearch(data?.entityLinkMap?.MILESTONE)
      ]).then((dataArr: any[]) => {
        setFilterOptions({
          rule: dataArr[0]?.map((item: string, index: number) => ({ value: index + 1, label: item })),
          stage: dataArr[1]?.map((item: string, index: number) => ({ value: index + 1, label: item })),
          milestone: dataArr[2]?.map((item: string, index: number) => ({ value: index + 1, label: item }))
        })
      })
    })
  }, [])

  useEffect(() => {
    if (
      !packageData?.preselectionDone &&
      options?.selectedFPEntries &&
      options.selectedFPEntries.length > 0 &&
      packageData?.id &&
      typeof setPackageData === 'function'
    ) {
      const initiallySelected = pluck(options.selectedFPEntries || [], 'id')
      if (initiallySelected?.length > 0) {
        setPackageData((prev: any) => {
          return {
            ...prev,
            preselectedArtifactIds: new Set(initiallySelected),
            preselectionDone: true
          }
        })
      }
    }
  }, [options.selectedFPEntries, packageData])

  useEffect(() => {
    onChange()
  }, [packageData, options])

  useEffect(() => {
    if (selectedArtifacts?.length) {
      selectedArtifacts.forEach((item) => {
        const parentDataIndex = optionsLocal?.fileplanEntries?.findIndex(
          (data: any) => data?.identifier === item?.parent?.parent?.identifier
        )
        if (parentDataIndex !== undefined && parentDataIndex >= 0) {
          updateOptionsData(item?.id, true, parentDataIndex)
        }
      })
    }
  }, [selectedArtifacts])

  const onChange = (optionsData: PackageOptions = options) => {
    optionsData.selectedFPEntries = context.api.packages.getLevel3Entries(
      optionsData.fileplanEntries || [],
      (entry: any) => entry.selected
    )
    setOptionsLocal(optionsData)
  }

  const updateOptionsData = (id: number | string, isChecked: boolean, parentArrayIndex: number) => {
    const optionsLocalData = cloneDeep(optionsLocal)
    const fileplanEntriesData = optionsLocalData?.fileplanEntries
    if (fileplanEntriesData && fileplanEntriesData[parentArrayIndex]) {
      toggleArtifactSelection(fileplanEntriesData[parentArrayIndex], id, isChecked)
      onChange(optionsLocalData)
    }
  }

  const toggleArtifactSelection = (node: any, id: number | string, isChecked: boolean) => {
    selectArtifactById(node, id, isChecked)
    if (node?.id === id) {
      node.selected = isChecked
    }
    if (node?.children && Array.isArray(node?.children)) {
      node.children?.forEach((child: any) => {
        toggleArtifactSelection(child, id, isChecked)
        if (isChecked && child?.children) {
          child.expanded = true
        }
      })
    }
  }

  const selectArtifactById = (data: any, id: number | string, isChecked: boolean): boolean => {
    if (data?.id ? data.id === id : data.identifier === id) {
      data.selected = isChecked

      if (!data.isArtifact) {
        data.expanded = true
        extendDescendantsRecursive(data, { selected: isChecked })
      }

      recalculateSelectionState(data.parent)
      return true
    }

    if (data?.children?.length) {
      for (const item of data.children) {
        if (selectArtifactById(item, id, isChecked)) {
          return true
        }
      }
    }
    return false
  }

  const extendDescendantsRecursive = (node: any, props: any) => {
    if (node.hasChildren) {
      node.children.forEach((entry: any) => {
        Object.assign(entry, props)
        extendDescendantsRecursive(entry, props)
      })
      node.containsContaining = props.selected ? node.children.length : 0
      node.containsSelected = props.selected ? node.children.length : 0
    }
  }

  const recalculateSelectionState = (node: any) => {
    if (!node) return

    if (node.children && node.children.length) {
      let selectedCount = 0
      let containingCount = 0

      node.children.forEach((child: any) => {
        if (child.selected) {
          selectedCount++
        }
        if (child.containsContaining > 0 || child.selected) {
          containingCount++
        }
      })

      node.containsSelected = selectedCount
      node.containsContaining = containingCount
      node.selected = selectedCount === node.children.length
    }

    recalculateSelectionState(node.parent)
  }

  const Option = (props: any) => {
    const input = props.selectProps.inputValue
    const option = props.data.label
    return (
      <components.Option {...props}>
        <input type="checkbox" name="artifacts" checked={props.isSelected} value={props.value} />
        {highlight_search(input, option)}
      </components.Option>
    )
  }

  const expandAncestors = (node: any) => {
    while (node) {
      node.expanded = true
      node = node.parent
    }
  }

  const updateSelectionsRecursively = (node: any, selectedIdsSet: Set<number>) => {
    if (!node) return

    if (node.isArtifact && node.id !== undefined) {
      node.selected = selectedIdsSet.has(node.id)
      node.containsSelected = node.selected ? 1 : 0
      node.containsContaining = node.selected ? 1 : 0
    } else if (node.children && node.children.length > 0) {
      let childSelectedCount = 0
      let childContainingCount = 0

      node.children.forEach((child: any) => {
        updateSelectionsRecursively(child, selectedIdsSet)
        if (child.selected) {
          childSelectedCount++
        }
        if (child.containsContaining > 0 || child.selected) {
          childContainingCount++
        }
      })

      node.containsSelected = childSelectedCount
      node.containsContaining = childContainingCount

      // Expand node if it contains any selected items
      if (childContainingCount > 0) {
        node.expanded = true
      }

      if (childSelectedCount === node.children.length) {
        node.selected = true
      } else {
        node.selected = false
      }
    }
  }

  function onArtifactChange(selectedOptions: any, actionMeta: any) {
    const optionsLocalClone = cloneDeep(optionsLocal)
    const selectedArtifactIdsSet = new Set(pluck(selectedOptions, 'id'))
    const allArtifacts = optionsLocalClone.filePlanLevel3Entries || []
    const selectedOptionId = actionMeta?.option?.id
    if (actionMeta?.action === 'deselect-option' && selectedOptionId) {
      setManuallySelectedArtifactIds((prev: Set<number>) => {
        const updatedSet = new Set(prev)
        updatedSet.delete(selectedOptionId)
        return updatedSet
      })
    } else if (actionMeta?.action === 'select-option' && selectedOptionId) {
      allArtifacts.forEach((item: any) => {
        if (selectedArtifactIdsSet.has(item.id)) {
          expandAncestors(item.parent)
        }
      })
      setManuallySelectedArtifactIds((prev: Set<number>) => {
        const updatedSet = new Set(prev)
        updatedSet.add(selectedOptionId)
        return updatedSet
      })
    }
    const fileplanEntries = optionsLocalClone.fileplanEntries
    if (fileplanEntries) {
      fileplanEntries.forEach((root: any) => {
        updateSelectionsRecursively(root, selectedArtifactIdsSet)
      })
    }
    onChange(optionsLocalClone)
    setSelectedArtifacts(Array.isArray(selectedOptions) && selectedOptions?.length ? selectedOptions : [])
  }

  const updateArtifactsBasedOnSelections = (
    selectedRules: any[] = [],
    selectedStages: any[] = [],
    selectedMilestones: any[] = []
  ) => {
    const optionsLocalClone = cloneDeep(optionsLocal)

    const allArtifacts = optionsLocalClone.filePlanLevel3Entries || []

    const filterSelectedArtifactIds = allArtifacts.reduce((acc: number[], item: any) => {
      if (selectedRules?.length || selectedStages?.length || selectedMilestones?.length) {
        const rulesLabelsArr = pluck(selectedRules, 'label')
        const stageLabelsArr = pluck(selectedStages, 'label')
        const milestoneLabelsArr = pluck(selectedMilestones, 'label')
        const matchesRule = rulesLabelsArr.length === 0 || rulesLabelsArr.includes(item.rule)
        const matchesStage = stageLabelsArr.length === 0 || stageLabelsArr.includes(item.stage)
        const matchesMilestone = milestoneLabelsArr.length === 0 || milestoneLabelsArr.includes(item.mileStone)
        if (matchesRule && matchesStage && matchesMilestone) {
          acc.push(item.id)
        }
      }
      return acc
    }, [])

    const filterSelectedArtifactIdsSet = new Set(filterSelectedArtifactIds)
    setFilterSelectedArtifactIds(filterSelectedArtifactIdsSet)
    const overlapExcludedPreSelectedSelection = packageData?.preselectedArtifactIds?.size
      ? excludeOverlap(filterSelectedArtifactIdsSet, packageData?.preselectedArtifactIds)
      : packageData?.preselectedArtifactIds || []

    const finalSelectedArtifactIdsSet = new Set<number>()
    Array.from(filterSelectedArtifactIdsSet).forEach((id) => finalSelectedArtifactIdsSet.add(id))
    Array.from(overlapExcludedPreSelectedSelection).forEach((id: number) => finalSelectedArtifactIdsSet.add(id))
    Array.from(manuallySelectedArtifactIds).forEach((id) => finalSelectedArtifactIdsSet.add(id))

    const overlapExcludedManualSelection = excludeOverlap(manuallySelectedArtifactIds, filterSelectedArtifactIdsSet)
    setManuallySelectedArtifactIds(overlapExcludedManualSelection)

    const fileplanEntriesForUpdate = optionsLocalClone.fileplanEntries
    if (fileplanEntriesForUpdate) {
      fileplanEntriesForUpdate.forEach((root: any) => {
        updateSelectionsRecursively(root, finalSelectedArtifactIdsSet)
      })
    }

    allArtifacts.forEach((item: any) => {
      if (finalSelectedArtifactIdsSet.has(item.id)) {
        expandAncestors(item.parent)
      }
    })

    onChange(optionsLocalClone)

    const finalSelectedArtifacts = allArtifacts.filter((item: any) => finalSelectedArtifactIdsSet.has(item.id))
    setSelectedArtifacts(finalSelectedArtifacts)
  }

  return (
    <>
      <Modal.Body>
        <div className="modal-flex">
          <div className="col-xs-5 pr-8">
            <div className="form-group w-100">
              <label>{translate('EMTF_RULE')}</label>
              <Select
                multi
                components={{ Option }}
                value={pluck(selectedFilters?.rule || [], 'value')}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                placeholder={translate('ETMF_LABEL_SELECT_RULE')}
                options={filterOptions?.rule}
                isClearable={false}
                name="rule"
                onChange={(selectedRule: any) => {
                  setSelectedFilters({
                    ...selectedFilters,
                    rule: selectedRule
                  })
                  updateArtifactsBasedOnSelections(selectedRule, selectedFilters.stage, selectedFilters.milestone)
                }}
                searchable
                id="j81b0822-3a72-48na-b17a-9a96ceb0dd2"
              />
            </div>
            <div className="form-group w-100">
              <label>{translate('EMTF_STAGE')}</label>
              <Select
                multi
                value={pluck(selectedFilters?.stage || [], 'value')}
                components={{ Option }}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                placeholder={translate('ETMF_LABEL_SELECT_STAGE')}
                options={filterOptions?.stage}
                isClearable={false}
                name="stage"
                onChange={(selectedStage: any) => {
                  setSelectedFilters({
                    ...selectedFilters,
                    stage: selectedStage
                  })
                  updateArtifactsBasedOnSelections(selectedFilters.rule, selectedStage, selectedFilters.milestone)
                }}
                searchable
                id="p81b9822-3a72-48na-b17a-9a92ceb0dk2"
              />
            </div>
            <div className="form-group w-100">
              <label>{translate('EMTF_MILESTONE')}</label>
              <Select
                multi
                value={pluck(selectedFilters?.milestone || [], 'value')}
                components={{ Option }}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                placeholder={translate('ETMF_LABEL_SELECT_MILESTONE')}
                options={filterOptions?.milestone}
                isClearable={false}
                name="milestone"
                onChange={(selectedMilestone: any) => {
                  setSelectedFilters({
                    ...selectedFilters,
                    milestone: selectedMilestone
                  })
                  updateArtifactsBasedOnSelections(selectedFilters.rule, selectedFilters.stage, selectedMilestone)
                }}
                searchable
                id="k51b0822-3a79-48na-bb7a-04a96ceb0fd2"
              />
            </div>
            <div className="form-group w-100">
              <label>{translate('SETTINGS_PACKAGE_SELECT_ARTIFACTS')}</label>
              <Select
                id="f51b0822-3a79-48aa-bb7a-04a96ceb0fd6"
                value={optionsLocal?.selectedFPEntries ? pluck(optionsLocal.selectedFPEntries, 'id') : []}
                options={transformToSelectOptions(options.filePlanLevel3Entries || [], 'id', 'name', null, true)}
                onChange={onArtifactChange}
                searchable
                components={{ Option }}
                multi
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                inputValue={arifactSearchString}
                onInputChange={(searchString: string, { action }: any) => {
                  if (action !== 'set-value') {
                    setArifactSearchString(searchString)
                  }
                }}
                onKeyDown={(evt: any) => {
                  if (!arifactSearchString?.length && evt?.key === 'Backspace') {
                    evt?.preventDefault()
                  }
                }}
                classNamePrefix="selected_package"
                isClearable={false}
              />
            </div>
          </div>
          <div className="col-xs-7">
            <div className="artifact-select-wrapper">
              <div className="artifact-selector-section-wrapper">
                <PackageArtifactSelector
                  options={optionsLocal}
                  preselectedData={optionsLocal?.selectedFPEntries}
                  updateOptionsData={updateOptionsData}
                  setManuallySelectedArtifactIds={setManuallySelectedArtifactIds}
                  filterSelectedArtifactIds={filterSelectedArtifactIds}
                />
              </div>
            </div>
            {isSubmitted && optionsLocal?.selectedFPEntries && optionsLocal?.selectedFPEntries?.length === 0 && (
              <p className="error">{translate('SETTINGS_PACKAGE_SELECT_ARTIFACTS_MIN_1_ERROR')}</p>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="">
          <button
            id="e68ab9cf-a708-457d-b00c-16d45005f222"
            data-action="cancel"
            className="btn btn-link text-capitalize"
            onClick={doClose}
            type="button"
          >
            {translate('CANCEL')}
          </button>
          {!isApplyPackageFlow && (
            <button
              id="93f93a8f-8283-442a-b937-0be72be199b0"
              className="btn btn-second text-capitalize"
              onClick={() => nextStep(packageData as any, optionsLocal, 'STEP1')}
            >
              {translate('BACK')}
            </button>
          )}
          <button
            id="57241e3a-1356-4a03-afb7-08149db48c28"
            data-action="save"
            onClick={() => {
              setIsSubmitted(true)
              if (optionsLocal?.selectedFPEntries?.length) {
                nextStep(packageData as any, optionsLocal, 'STEP3')
                if (isApplyPackageFlow) {
                  doClose()
                }
              }
            }}
            className="btn btn-primary text-capitalize"
            type="submit"
          >
            {translate(isApplyPackageFlow ? 'ACTION_ADD' : 'NEXT')}
          </button>
        </div>
      </Modal.Footer>
    </>
  )
}

export default EditPackageStep2
